import { Logger } from "../../../../../../../lib/logger/Logger";
import { CustomTable } from "../../../../../components/custom-table/custom-table.component";
import { TableConfig } from "../../../../../types/types";
import { jobItemsTableConfigDefault } from "./job-items-table-config-default";
import { JobItemEntityType } from "../../../../../../../lib/supabase/supabaseTypes";
import { TextField } from "@mui/material";
import { JobItemsTariffsSelection } from "./job-items-table-components/job-item-tariffs-selection.component";
import { JobItemsArticlesSelection } from "./job-items-table-components/job-items-articles-selection.component";
import { useEffect, useState } from "react";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { JobStatusEnum } from "@/lib/types/job";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { JobDocumentTypeEnum } from "../../job-document.types";
import { CodeSuggestions } from "./code-suggestions.component";
import { useTemplatesStore } from "@/dentlab/src/store/Templates";

function addJobItemAllowed(status: JobStatusEnum) {
	const allowed = status === JobStatusEnum.IN_PROGRESS;
	if (!allowed) {
		showNotification({
			message:
				"Positionen können nur bearbeitet werden, wenn der Auftrag noch nicht abgeschlossen ist.",
			type: "error",
		});
	}
	return allowed;
}

export const JobItemsTable: React.FC<{
	jobDocumentId: number;
	status: JobStatusEnum;
	jobDocumentType: JobDocumentTypeEnum;
	disabled?: boolean;
}> = ({ jobDocumentId, jobDocumentType, status, disabled = false }) => {
	const { gridConfig, updateGridConfig } = useCentralStore((state) => ({
		gridConfig: state.gridConfig,
		updateGridConfig: state.updateGridConfig,
	}));

	const {
		addTariff,
		addArticle,
		deleteJobItems,
		pasteJobItems,
		changeJobItem,
		jobItemsForDocuments,
		fetchJobItems,
	} = useJobStore((state) => ({
		jobItemsForDocuments: state.jobItemsForDocuments,
		addTariff: state.addTariff,
		addArticle: state.addArticle,
		deleteJobItems: state.deleteJobItems,
		pasteJobItems: state.pasteJobItems,
		changeJobItem: state.changeJobItem,
		fetchJobItems: state.fetchJobItems,
	}));

	useEffect(() => {
		fetchJobItems(jobDocumentId);
	}, []);

	const jobItems = jobItemsForDocuments?.[jobDocumentId] ?? [];
	const handleRowChange = (newRow: any) => {
		if (addJobItemAllowed(status)) changeJobItem(newRow);
	};

	const handleAddTariff = (value: string, options?: { force: boolean }) => {
		if (addJobItemAllowed(status)) addTariff(value, jobDocumentId, options);
	};
	const handleAddArticle = (value: string, options?: { force: boolean }) => {
		if (addJobItemAllowed(status))
			addArticle(value, jobDocumentId, options);
	};
	const handleAddTemplate = (value: string, options?: { force: boolean }) => {
		//TODO Define type finder in own function or refactor store
		if (addJobItemAllowed(status)) {
			const { tariffs } = useCentralStore.getState();

			const templates = useTemplatesStore.getState().templatesLookup;
			const template = Object.values(templates).find(
				(t) => t.code === value
			);

			template?.template_items.map((item) => {
				if (item.code_e in tariffs) {
					addTariff(item.code_e, jobDocumentId, { force: false });
				} else {
					addArticle(item.code_e, jobDocumentId, { force: false });
				}
			});
		}
	};

	const handleRowDelete = (rowIds: string[]) => {
		if (addJobItemAllowed(status)) deleteJobItems(rowIds, jobDocumentId);
	};

	const handlePasteRows = (pastedRows: any[]) => {
		if (addJobItemAllowed(status)) pasteJobItems(pastedRows, jobDocumentId);
	};

	const handleCutOrDeleteRows = (cutRows: any[]) => {
		if (addJobItemAllowed(status))
			handleRowDelete(cutRows.map((row) => row.id));
	};

	const handleConfigChange = async (newConfig: TableConfig) => {
		updateGridConfig(newConfig);
	};

	return (
		<div>
			<CustomTable
				rows={jobItems}
				userConfig={gridConfig}
				defaultConfig={jobItemsTableConfigDefault}
				onConfigChange={handleConfigChange}
				onRowChange={handleRowChange}
				onSelectedChange={(selected) => {
					Logger.log("onSelectedChange", selected);
				}}
				onPasteRows={handlePasteRows}
				onCutRows={handleCutOrDeleteRows}
				dataCompatibility={["job_items"]}
				onDeleteRows={handleCutOrDeleteRows}
				toolbarChildren={
					<Toolbar
						onAddTariff={handleAddTariff}
						onAddArticle={handleAddArticle}
						onAddTemplate={handleAddTemplate}
						jobItemCodes={jobItems}
						jobDocumentId={jobDocumentId}
						jobDocumentType={jobDocumentType}
					/>
				}
				disabled={disabled}
			/>
			{!disabled && (
				<CodeSuggestions
					onAddTariff={handleAddTariff}
					onAddArticle={handleAddArticle}
					jobItemCodes={jobItems.map((item) => item.code_e)}
				/>
			)}
		</div>
	);
};

// TODO: NON-DEMO add density selection
// import { CustomDensitySelector } from "../../../../components/custom-density-selector.component";
// <CustomDensitySelector density="standard" onChange={() => {}} />

export const Toolbar: React.FC<{
	onAddTariff: (value: string) => void;
	onAddArticle: (value: string) => void;
	onAddTemplate: (value: string) => void;
	jobItemCodes: { code_e: string }[];
	jobDocumentId: number;
	jobDocumentType: JobDocumentTypeEnum | "template";
}> = ({
	onAddTariff,
	onAddArticle,
	onAddTemplate,
	jobItemCodes,
	jobDocumentType,
	jobDocumentId,
}) => {
	const { articles, tariffs } = useCentralStore((state) => ({
		articles: Object.values(state.articles).sort((a, b) =>
			a.code_e.localeCompare(b.code_e)
		),
		tariffs: Object.values(state.tariffs).sort((a, b) =>
			a.code_e.localeCompare(b.code_e)
		),
	}));

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "row",
				alignItems: "center",
				justifyContent: "space-between",
				width: "100%",
				height: "30.75px", // TODO: fix this hack
			}}
		>
			<div className="flex gap-2">
				{jobDocumentType !== JobDocumentTypeEnum.MATERIALS && (
					<div className="flex gap-4">
						<TarifcodeButton
							onSubmit={(val) => {
								onAddTariff(val);
							}}
						/>
					</div>
				)}
				<div>
					<ArticleCodeButton
						onSubmit={(val) => {
							onAddArticle(val);
						}}
					/>
				</div>
				{jobDocumentType !== JobDocumentTypeEnum.MATERIALS && (
					<div>
						<TemplatecodeButton
							onSubmit={(val) => {
								onAddTemplate(val);
							}}
						/>
					</div>
				)}
			</div>
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					gap: "10px",
				}}
			>
				{jobDocumentType !== JobDocumentTypeEnum.MATERIALS && (
					<JobItemsTariffsSelection
						jobItemCodes={jobItemCodes}
						rows={tariffs}
						onSelect={onAddTariff}
						jobDocumentId={jobDocumentId}
						isNewStyle={jobDocumentType === "template"}
					/>
				)}
				<JobItemsArticlesSelection
					rows={articles}
					label="Artikel"
					onSelect={onAddArticle}
					jobItemCodes={jobItemCodes}
					jobDocumentId={jobDocumentId}
					isNewStyle={jobDocumentType === "template"}
				/>
			</div>
		</div>
	);
};

// Reusable component
const CodeButton: React.FC<{
	onSubmit: (value: string) => void;
	placeholder: string;
	width: string;
}> = ({ onSubmit, placeholder, width }) => {
	const [value, setValue] = useState<string>("");

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setValue(event.target.value);
	};

	const handleClick = () => {
		if (value) {
			onSubmit(value);
			setValue("");
		} else {
			showNotification({
				message: "Bitte einen Wert eingeben",
				type: "error",
			});
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			handleClick();
		}
	};

	return (
		<div className="flex justify-center items-center gap-1">
			<TextField
				value={value || ""}
				onChange={handleChange}
				placeholder={placeholder}
				variant="outlined"
				size="small"
				sx={{
					width: width,
					"& .MuiInputBase-input": {
						padding: "3px 9px",
						height: "24px",
					},
				}}
				InputProps={{
					onKeyDown: handleKeyPress,
					sx: {
						fontSize: "14px",
					},
				}}
			/>
		</div>
	);
};

// Specialized components using the reusable CodeButton
export const ArticleCodeButton: React.FC<{
	onSubmit: (value: string) => void;
}> = ({ onSubmit }) => (
	<CodeButton onSubmit={onSubmit} placeholder="Artikelcode" width="90px" />
);

export const TarifcodeButton: React.FC<{
	onSubmit: (value: string) => void;
}> = ({ onSubmit }) => (
	<CodeButton onSubmit={onSubmit} placeholder="Tarifcode" width="80px" />
);

export const TemplatecodeButton: React.FC<{
	onSubmit: (value: string) => void;
}> = ({ onSubmit }) => (
	<CodeButton onSubmit={onSubmit} placeholder="Vorlagencode" width="105px" />
);
